import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Row } from '@/lib/supabase'
import { pipelineKeys } from '@/features/pipeline/queries'
import { resolveRules, type PricingRules, type QuoteBreakdown } from '@/lib/pricing'
import type { IsoDate } from '@/lib/dates'
import { isFilled, lineAmount, type EditableLine, type LineTotals } from '@/lib/lineItems'

export type Package = Row<'packages'>
export type Addon = Row<'addons'>
export type Quote = Row<'quotes'>
export type QuoteLineItem = Row<'quote_line_items'>

export const quoteKeys = {
  packages: ['packages'] as const,
  addons: ['addons'] as const,
  rules: ['pricing-rules'] as const,
  forJob: (jobId: string) => ['quotes', 'job', jobId] as const,
  detail: (id: string) => ['quote', id] as const,
}

export function usePackages(includeInactive = false) {
  return useQuery({
    queryKey: [...quoteKeys.packages, includeInactive],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Package[]> => {
      let query = supabase.from('packages').select('*').order('sort_order')
      if (!includeInactive) query = query.eq('active', true)

      const { data, error } = await query
      if (error) throw error
      return data
    },
  })
}

export function useAddons(includeInactive = false) {
  return useQuery({
    queryKey: [...quoteKeys.addons, includeInactive],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Addon[]> => {
      let query = supabase.from('addons').select('*').order('sort_order')
      if (!includeInactive) query = query.eq('active', true)

      const { data, error } = await query
      if (error) throw error
      return data
    },
  })
}

/**
 * The pricing rules in force on a given date.
 *
 * Every row is fetched and the resolution happens client-side in the pure
 * `resolveRules`, rather than filtering in SQL. That keeps the "which rule
 * applies" logic in one tested place instead of split between a query and a
 * function, and the table holds a few dozen rows at most.
 */
export function usePricingRules(onDate: IsoDate) {
  return useQuery({
    queryKey: [...quoteKeys.rules, onDate],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<{ rules: PricingRules; rows: Row<'pricing_rules'>[] }> => {
      const { data, error } = await supabase.from('pricing_rules').select('*').order('effective_from')
      if (error) throw error

      return { rules: resolveRules(data, onDate), rows: data }
    },
  })
}

/** True while the seeded placeholders have not been reviewed. */
export function rulesArePlaceholders(rows: Row<'pricing_rules'>[] | undefined): boolean {
  return (rows ?? []).some((row) => row.note?.startsWith('PLACEHOLDER') ?? false)
}

// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------

export interface DocumentClient {
  id: string
  first_name: string
  last_name: string | null
  email: string | null
  phone: string | null
}

export interface DocumentProject {
  id: string
  kind: Row<'jobs'>['kind']
  title: string | null
  event_date: string
  job_number: number
  distance_km: number | null
  primary_contact: { first_name: string; last_name: string | null } | null
  partner_contact: { first_name: string; last_name: string | null } | null
}

export interface QuoteWithLines extends Quote {
  line_items: QuoteLineItem[]
  /** The client (29 Oct 2026). */
  contact: DocumentClient | null
  /** The project, if any. */
  job: DocumentProject | null
}

export const CLIENT_FIELDS = 'id, first_name, last_name, email, phone'
export const PROJECT_FIELDS = `id, kind, title, event_date, job_number, distance_km,
  primary_contact:contacts!jobs_primary_contact_id_fkey ( first_name, last_name ),
  partner_contact:contacts!jobs_partner_contact_id_fkey ( first_name, last_name )`

const QUOTE_SELECT = `*, line_items:quote_line_items(*), contact:contacts!quotes_contact_id_fkey ( ${CLIENT_FIELDS} ), job:jobs!quotes_job_id_fkey ( ${PROJECT_FIELDS} )`

export function useJobQuotes(jobId: string | undefined) {
  return useQuery({
    queryKey: quoteKeys.forJob(jobId ?? ''),
    enabled: Boolean(jobId),
    queryFn: async (): Promise<QuoteWithLines[]> => {
      const { data, error } = await supabase
        .from('quotes')
        .select(QUOTE_SELECT)
        .eq('job_id', jobId ?? '')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as unknown as QuoteWithLines[]
    },
  })
}

/** Every quote, newest first, for Documents › Quotes. */
export function useAllQuotes() {
  return useQuery({
    queryKey: ['quotes', 'all'] as const,
    queryFn: async (): Promise<QuoteWithLines[]> => {
      const { data, error } = await supabase.from('quotes').select(QUOTE_SELECT).order('created_at', { ascending: false })
      if (error) throw error
      return data as unknown as QuoteWithLines[]
    },
  })
}

export function useQuote(id: string | undefined) {
  return useQuery({
    queryKey: quoteKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async (): Promise<QuoteWithLines> => {
      const { data, error } = await supabase.from('quotes').select(QUOTE_SELECT).eq('id', id ?? '').single()
      if (error) throw error
      const quote = data as unknown as QuoteWithLines
      quote.line_items.sort((a, b) => a.sort_order - b.sort_order)
      return quote
    },
  })
}

export interface SaveQuoteInput {
  quoteId?: string | undefined
  jobId: string
  packageId: string | null
  breakdown: QuoteBreakdown
  rulesSnapshot: unknown
  validUntil: string | null
  coverNote: string | null
  staffId: string
}

/**
 * Writes a draft quote and its lines.
 *
 * The lines are deleted and rewritten rather than diffed. A quote has a handful
 * of lines, they are entirely derived from the engine, and a diff would be more
 * code with more ways to leave a stale line behind. Both statements go through
 * PostgREST separately, so a failure between them leaves a quote with no lines —
 * visible immediately in the builder, and fixed by pressing save again.
 */
export function useSaveQuote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SaveQuoteInput): Promise<string> => {
      const header = {
        job_id: input.jobId,
        package_id: input.packageId,
        subtotal: input.breakdown.subtotal,
        vat: input.breakdown.vat,
        total: input.breakdown.total,
        rules_snapshot: input.rulesSnapshot as never,
        valid_until: input.validUntil,
        cover_note: input.coverNote,
        created_by: input.staffId,
      }

      let quoteId = input.quoteId

      if (quoteId) {
        const { error } = await supabase.from('quotes').update(header).eq('id', quoteId)
        if (error) throw error

        const { error: clearError } = await supabase.from('quote_line_items').delete().eq('quote_id', quoteId)
        if (clearError) throw clearError
      } else {
        const { data, error } = await supabase.from('quotes').insert(header).select('id').single()
        if (error) throw error
        quoteId = data.id
      }

      const lines = input.breakdown.lines.map((line, index) => ({
        quote_id: quoteId as string,
        source: line.source,
        source_id: line.sourceId ?? null,
        description: line.description,
        quantity: line.quantity,
        unit_price: line.unitPrice,
        line_total: line.total,
        detail: line.detail ?? null,
        sort_order: index,
      }))

      if (lines.length > 0) {
        const { error } = await supabase.from('quote_line_items').insert(lines)
        if (error) throw error
      }

      return quoteId
    },

    onSuccess: (_id, input) => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.forJob(input.jobId) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.job(input.jobId) })
    },

    onError: (error) => {
      toast.error('Could not save the quote', { description: describeError(error) })
    },
  })
}

/**
 * Saves a draft quote typed line by line (29 Oct 2026): the header and its
 * totals, then the lines replaced, as useSaveQuote does.
 */
export function useSaveQuoteDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      quoteId: string
      lines: EditableLine[]
      totals: LineTotals
      validUntil: string | null
      coverNote: string | null
    }) => {
      const { error } = await supabase
        .from('quotes')
        .update({
          subtotal: Math.max(0, input.totals.subtotal),
          vat: Math.max(0, input.totals.vat),
          total: input.totals.total,
          valid_until: input.validUntil,
          cover_note: input.coverNote,
        })
        .eq('id', input.quoteId)
        .eq('status', 'draft')
      if (error) throw error
      const { error: clearError } = await supabase.from('quote_line_items').delete().eq('quote_id', input.quoteId)
      if (clearError) throw clearError
      const rows = input.lines.filter(isFilled).map((line, index) => ({
        quote_id: input.quoteId,
        source: line.source,
        source_id: line.sourceId,
        description: line.description.trim(),
        detail: line.detail.trim() || null,
        quantity: line.quantity,
        unit_price: line.unitPrice,
        line_total: lineAmount(line.quantity, line.unitPrice),
        sort_order: index,
      }))
      if (rows.length) {
        const { error: insertError } = await supabase.from('quote_line_items').insert(rows)
        if (insertError) throw insertError
      }
    },
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: ['quotes'] })
      void queryClient.invalidateQueries({ queryKey: quoteKeys.detail(input.quoteId) })
    },
    onError: (error) => toast.error('Could not save the quote', { description: describeError(error) }),
  })
}

export function useUpdateQuoteStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Quote['status']; jobId: string | null }) => {
      const { error } = await supabase.from('quotes').update({ status }).eq('id', id)
      if (error) throw error
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['quotes'] })
      void queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) })
    },
    onError: (error) => {
      toast.error('Could not update the quote', { description: describeError(error) })
    },
  })
}

/**
 * Builds the PDF server side and opens it.
 *
 * The document is rendered from the quote's stored lines, not recalculated, so
 * a quote sent in March prints the same numbers in September. The returned URL
 * is signed and short-lived — the bucket is private and nothing in it is ever
 * linked to directly.
 */
export function useGenerateQuotePdf() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ quoteId }: { quoteId: string; jobId: string | null }): Promise<string> => {
      const { data, error } = await supabase.functions.invoke<{ signedUrl: string; error?: string }>(
        'generate-quote-pdf',
        { body: { quoteId } },
      )

      // A non-2xx from an edge function arrives as a FunctionsHttpError whose
      // body holds the real message. Without this the user sees the generic
      // "Edge Function returned a non-2xx status code", which says nothing.
      if (error) {
        const detail = await readFunctionError(error)
        throw new Error(detail)
      }

      if (!data?.signedUrl) throw new Error('The PDF was generated but no link came back.')
      return data.signedUrl
    },

    onSuccess: (_url, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['quotes'] })
      void queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.quoteId) })
    },

    onError: (error) => {
      toast.error('Could not build the PDF', { description: describeError(error) })
    },
  })
}

async function readFunctionError(error: unknown): Promise<string> {
  const response = (error as { context?: Response }).context

  if (response && typeof response.json === 'function') {
    try {
      const body = (await response.json()) as { error?: string }
      if (body.error) return body.error
    } catch {
      /* fall through to the generic message */
    }
  }

  return error instanceof Error ? error.message : 'The PDF could not be generated.'
}

export function useDeleteQuote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id }: { id: string; jobId: string | null }) => {
      const { error } = await supabase.from('quotes').delete().eq('id', id).eq('status', 'draft')
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
    onError: (error) => {
      toast.error('Could not delete the quote', { description: describeError(error) })
    },
  })
}
